import hubspot from '@/assets/images/hubspot.png';
import * as Sentry from '@sentry/react';
import {
  HubSpotIntegration,
  NewEventIntegration,
  NewLeadIntegration,
  NewPartialLeadIntegration,
  ObjectParamMapping,
} from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import { diff } from 'deep-diff';
import { Key, Link as LinkIcon } from 'iconoir-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuditLog } from '../contexts/AuditLogContext';
import { AuthContext } from '../contexts/AuthContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import {
  ConnectedAccountSummary,
  createHubSpotIntegration,
  getHubSpotProperties,
  HubSpotProperty,
  initHubSpotOAuth,
  listConnectedAccounts,
  updateHubSpotIntegration,
} from '../services/IntegrationsService';
import ConnectHubSpotPrivateAppModal from './ConnectHubSpotPrivateAppModal';
import UpdateHubSpotPrivateAppTokenModal from './UpdateHubSpotPrivateAppTokenModal';
import { HubSpotMappingForm } from './hubspot/HubSpotMappingForm';

interface Props {
  integrationId?: string;
  integration?: HubSpotIntegration;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  forceDynamicPagesConnection?: string[];
  /** Pre-selected connected account ID (e.g., from OAuth callback) */
  preselectedConnectedAccountId?: string;
}

type HubSpotIntegrationData = Pick<
  HubSpotIntegration,
  | 'name'
  | 'status'
  | 'type'
  | 'dynamicPageIds'
  | 'formIds'
  | 'fieldMappings'
  | 'connectedAccountId'
> &
  Partial<NewEventIntegration | NewLeadIntegration | NewPartialLeadIntegration>;

export const EditHubSpotIntegrationModal = ({
  open,
  onClose,
  onSaved,
  integrationId,
  integration,
  forceDynamicPagesConnection,
  preselectedConnectedAccountId,
}: Props) => {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const { firebaseUser } = useContext(AuthContext);
  const { dynamicPages, forms } = useDashboardContext();
  const auditLog = useAuditLog();

  // Connected accounts state
  const [connectedAccounts, setConnectedAccounts] = useState<
    ConnectedAccountSummary[]
  >([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedConnectedAccountId, setSelectedConnectedAccountId] = useState<
    string | undefined
  >(preselectedConnectedAccountId);

  // Integration data state
  const [data, setData] = useState<Partial<HubSpotIntegrationData>>(
    integration ?? {},
  );
  const [objectMappings, setObjectMappings] = useState<ObjectParamMapping[]>(
    [],
  );
  const [hubspotProperties, setHubspotProperties] = useState<HubSpotProperty[]>(
    [],
  );
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connectingOAuth, setConnectingOAuth] = useState(false);

  // Private App modal states
  const [showPrivateAppModal, setShowPrivateAppModal] = useState(false);
  const [showUpdateTokenModal, setShowUpdateTokenModal] = useState(false);
  const [updateTokenAccount, setUpdateTokenAccount] =
    useState<ConnectedAccountSummary | null>(null);

  // Load connected accounts when modal opens
  useEffect(() => {
    if (open && selectedAccount) {
      setLoadingAccounts(true);
      listConnectedAccounts(selectedAccount.id, 'hubspot')
        .then((accounts) => {
          setConnectedAccounts(accounts);
          // If we have a preselected account or integration has one, use it
          if (preselectedConnectedAccountId) {
            setSelectedConnectedAccountId(preselectedConnectedAccountId);
          } else if (integration?.connectedAccountId) {
            setSelectedConnectedAccountId(integration.connectedAccountId);
          } else if (accounts.length === 1) {
            // Auto-select if only one account
            setSelectedConnectedAccountId(accounts[0].id);
          }
        })
        .catch((error) => {
          console.error('Failed to load connected accounts:', error);
          Sentry.captureException(error);
        })
        .finally(() => {
          setLoadingAccounts(false);
        });
    }
  }, [
    open,
    selectedAccount,
    preselectedConnectedAccountId,
    integration?.connectedAccountId,
  ]);

  // Load integration data when it changes
  useEffect(() => {
    if (integration) {
      setData({
        name: integration.name,
        status: integration.status,
        type: integration.type,
        dynamicPageIds: integration.dynamicPageIds,
        formIds: integration.formIds,
        connectedAccountId: integration.connectedAccountId,
        eventTypes:
          'eventTypes' in integration ? integration.eventTypes : undefined,
      });
      setSelectedConnectedAccountId(integration.connectedAccountId);

      // Load existing field mappings directly
      if (integration.fieldMappings) {
        setObjectMappings(integration.fieldMappings);
      }
    } else {
      setData({});
      // Default mappings for new integration
      setObjectMappings([
        {
          key: 'email',
          value: { type: 'param', value: 'data.form.email.value' },
        },
        {
          key: 'firstname',
          value: { type: 'param', value: 'data.form.name.value' },
        },
        {
          key: 'phone',
          value: { type: 'param', value: 'data.form.phone.value' },
        },
      ]);
    }
  }, [integration]);

  // Load HubSpot properties when a connected account is selected
  useEffect(() => {
    if (open && selectedConnectedAccountId && selectedAccount) {
      setLoadingProperties(true);
      getHubSpotProperties(selectedAccount.id, selectedConnectedAccountId)
        .then((result) => {
          setHubspotProperties(result.properties);
        })
        .catch((error) => {
          console.error('Failed to load HubSpot properties:', error);
          Sentry.captureException(error);
        })
        .finally(() => {
          setLoadingProperties(false);
        });
    }
  }, [open, selectedConnectedAccountId, selectedAccount]);

  const onConnectHubSpot = useCallback(async () => {
    if (!selectedAccount || !firebaseUser) return;

    setConnectingOAuth(true);
    try {
      const result = await initHubSpotOAuth(
        selectedAccount.id,
        firebaseUser.uid,
        window.location.pathname,
      );
      // Redirect to HubSpot OAuth
      window.location.href = result.authUrl;
    } catch (error) {
      show('Failed to initiate HubSpot connection', 'warn');
      console.error(error);
      Sentry.captureException(error);
      setConnectingOAuth(false);
    }
  }, [selectedAccount, firebaseUser, show]);

  // Handle successful Private App connection
  const onPrivateAppConnected = useCallback(
    async (connectedAccountId: string) => {
      // Refresh the accounts list
      if (selectedAccount) {
        try {
          const accounts = await listConnectedAccounts(
            selectedAccount.id,
            'hubspot',
          );
          setConnectedAccounts(accounts);
          setSelectedConnectedAccountId(connectedAccountId);
        } catch (error) {
          console.error('Failed to refresh connected accounts:', error);
        }
      }
    },
    [selectedAccount],
  );

  // Handle successful token update
  const onTokenUpdated = useCallback(async () => {
    // Refresh the accounts list to update status
    if (selectedAccount) {
      try {
        const accounts = await listConnectedAccounts(
          selectedAccount.id,
          'hubspot',
        );
        setConnectedAccounts(accounts);
      } catch (error) {
        console.error('Failed to refresh connected accounts:', error);
      }
    }
  }, [selectedAccount]);

  const onSave = useCallback(async () => {
    if (!selectedAccount || !selectedConnectedAccountId) {
      show('Please select a HubSpot account', 'warn');
      return;
    }

    if (data.type === 'new_event' && !data.eventTypes?.length) {
      show('Event Types are required', 'warn');
      return;
    }

    // Filter out incomplete mappings
    const validMappings = objectMappings.filter(
      (m) => m.key && m.value.value !== undefined && m.value.value !== '',
    );

    // At least one field mapping is required
    if (validMappings.length === 0) {
      show('At least one field mapping is required', 'warn');
      return;
    }

    // Email mapping is required for HubSpot contact upsert
    if (!validMappings.some((m) => m.key === 'email')) {
      show('Email field mapping is required', 'warn');
      return;
    }

    setSaving(true);

    try {
      if (integrationId) {
        // Update existing integration
        // Filter status to only allowed values for update
        const statusForUpdate =
          data.status === 'active' || data.status === 'disabled'
            ? data.status
            : undefined;

        await updateHubSpotIntegration(
          selectedAccount.id,
          integrationId,
          selectedConnectedAccountId,
          {
            name: data.name,
            type: data.type,
            status: statusForUpdate,
            dynamicPageIds: forceDynamicPagesConnection
              ? forceDynamicPagesConnection
              : data.dynamicPageIds || [],
            formIds: data.type !== 'new_event' ? data.formIds || [] : [],
            fieldMappings: validMappings,
            eventTypes: data.type === 'new_event' ? data.eventTypes : undefined,
          },
        );

        show('HubSpot integration updated', 'success');

        auditLog.log(
          'update',
          'Update HubSpot integration',
          'integration',
          integrationId,
          data.name,
          integration
            ? diff(integration, { ...integration, ...data })
            : undefined,
        );
      } else {
        // Create new integration
        await createHubSpotIntegration(
          selectedAccount.id,
          selectedConnectedAccountId,
          {
            name: data.name || 'HubSpot Integration',
            type: data.type || 'new_lead',
            status: 'active',
            dynamicPageIds: forceDynamicPagesConnection
              ? forceDynamicPagesConnection
              : data.dynamicPageIds || [],
            formIds: data.type !== 'new_event' ? data.formIds || [] : [],
            fieldMappings: validMappings,
            // eventTypes: data.type === 'new_event' ? data.eventTypes : undefined,
          },
        );

        show('HubSpot integration created', 'success');

        auditLog.log(
          'create',
          'Create HubSpot integration',
          'integration',
          'new',
          data.name,
        );
      }

      onClose();
      onSaved?.();
    } catch (e) {
      show('Failed to save HubSpot integration', 'warn');
      console.error(e);
      Sentry.captureException(e);
    } finally {
      setSaving(false);
    }
  }, [
    auditLog,
    data,
    objectMappings,
    forceDynamicPagesConnection,
    integration,
    integrationId,
    onClose,
    onSaved,
    selectedAccount,
    selectedConnectedAccountId,
    show,
  ]);

  const actions: ModalActionData[] = useMemo(() => {
    // No connected accounts - show connect options
    if (connectedAccounts.length === 0 && !loadingAccounts) {
      return [
        {
          title: 'Cancel',
          type: 'secondary',
          onClick: () => onClose(),
        },
        {
          title: 'Connect with Private App',
          type: 'secondary',
          onClick: () => setShowPrivateAppModal(true),
        },
        {
          title: 'Connect with OAuth',
          type: 'primary',
          loading: connectingOAuth,
          onClick: () => onConnectHubSpot(),
        },
      ];
    }

    return [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: integrationId ? 'Update' : 'Create Integration',
        type: 'primary',
        loading: saving,
        disabled: !selectedConnectedAccountId,
        onClick: () => onSave(),
      },
    ];
  }, [
    connectedAccounts.length,
    loadingAccounts,
    integrationId,
    saving,
    connectingOAuth,
    selectedConnectedAccountId,
    onClose,
    onConnectHubSpot,
    onSave,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="3xl"
      title={
        integrationId
          ? 'Configure HubSpot Integration'
          : 'New HubSpot Integration'
      }
    >
      <div className="mt-0 flex flex-col gap-4">
        {loadingAccounts ? (
          <div className="py-8 text-center text-gray-500">
            Loading connected accounts...
          </div>
        ) : connectedAccounts.length === 0 ? (
          // No connected accounts - show connect prompt
          <div className="flex flex-col items-center gap-4 py-8">
            <img src={hubspot} alt="HubSpot" className="h-16 w-16" />
            <p className="text-center text-gray-600">
              Connect your HubSpot account to automatically sync leads from your
              SuperFunnel landing pages to HubSpot CRM.
            </p>
          </div>
        ) : (
          // Show connected accounts and integration configuration
          <>
            {/* Connected Account Selection */}
            <div className="flex flex-col gap-2">
              <FieldTitle title="HubSpot Account" />
              <Select
                value={selectedConnectedAccountId ?? ''}
                onValueChange={(value) => {
                  if (value === '__connect_oauth__') {
                    onConnectHubSpot();
                  } else if (value === '__connect_private_app__') {
                    setShowPrivateAppModal(true);
                  } else {
                    setSelectedConnectedAccountId(value);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a HubSpot account" />
                </SelectTrigger>
                <SelectContent>
                  {connectedAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-2 w-2 rounded-full ${
                            account.status === 'active'
                              ? 'bg-green-500'
                              : account.status === 'expired'
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                          }`}
                        />
                        <span>{account.name}</span>
                        {account.authType === 'private_app' && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                            Private App
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="__connect_oauth__">
                    <div className="flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      Connect with OAuth
                    </div>
                  </SelectItem>
                  <SelectItem value="__connect_private_app__">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Connect with Private App Token
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Show Update Token button for Private App accounts */}
              {selectedConnectedAccountId &&
                (() => {
                  const selectedAccount = connectedAccounts.find(
                    (a) => a.id === selectedConnectedAccountId,
                  );
                  return (
                    selectedAccount?.authType === 'private_app' && (
                      <button
                        type="button"
                        onClick={() => {
                          setUpdateTokenAccount(selectedAccount);
                          setShowUpdateTokenModal(true);
                        }}
                        className="text-brand-600 hover:text-brand-700 self-start text-sm hover:underline"
                      >
                        Update access token
                      </button>
                    )
                  );
                })()}
            </div>

            {/* Show integration config only when account is selected */}
            {selectedConnectedAccountId && (
              <>
                <Input
                  title="Integration Name"
                  value={data.name}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                  placeholder='e.g. "Lead Sync to HubSpot"'
                />

                <div className="flex flex-col gap-2">
                  <FieldTitle title="Trigger" />
                  <Select
                    disabled={!!integration}
                    value={data.type ?? 'new_lead'}
                    onValueChange={(value: HubSpotIntegration['type']) =>
                      setData({ ...data, type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_lead">New Lead</SelectItem>
                      <SelectItem value="new_partial_lead">
                        Partial Lead
                      </SelectItem>
                      {/* <SelectItem value="new_event">Event</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>

                {/* {data.type === 'new_event' && (
                  <div className="flex flex-col gap-1">
                    <FieldTitle title="Event Types" />
                    <ChipsAutocomplete
                      placeholder="Select event types"
                      options={['*', 'view', 'conversion', 'button-click']}
                      selected={data.eventTypes ?? []}
                      onChange={(values) =>
                        setData({ ...data, eventTypes: values as any })
                      }
                      getValue={(o: string) =>
                        o === '*'
                          ? 'All Events'
                          : o.charAt(0).toUpperCase() + o.slice(1)
                      }
                    />
                  </div>
                )} */}

                <Separator />

                {/* Field Mappings */}
                <div className="flex flex-col gap-2">
                  <FieldTitle title="Field Mappings" />

                  {loadingProperties ? (
                    <div className="py-4 text-center text-gray-500">
                      Loading HubSpot properties...
                    </div>
                  ) : (
                    <HubSpotMappingForm
                      type={data.type}
                      objectMapping={objectMappings}
                      onChange={setObjectMappings}
                      hubspotProperties={hubspotProperties}
                    />
                  )}
                </div>

                <Separator />

                {!forceDynamicPagesConnection && (
                  <div className="flex flex-col gap-1">
                    <FieldTitle title="Landing Pages" />
                    <p className="mb-1 text-sm text-gray-500">
                      Leave empty to apply to all landing pages
                    </p>
                    <ChipsAutocomplete
                      modal
                      placeholder="Select landing pages"
                      options={dynamicPages ?? []}
                      selected={
                        dynamicPages?.filter((p) =>
                          data.dynamicPageIds?.includes(p.id),
                        ) ?? []
                      }
                      onChange={(values) =>
                        setData({
                          ...data,
                          dynamicPageIds: values.map((v) => v.id),
                        })
                      }
                      getValue={(o) => o.data()?.name || o.id}
                    />
                  </div>
                )}

                {data.type !== 'new_event' && (
                  <div className="flex flex-col gap-1">
                    <FieldTitle title="Forms" />
                    <p className="mb-1 text-sm text-gray-500">
                      Leave empty to apply to all forms
                    </p>
                    <ChipsAutocomplete
                      modal
                      placeholder="Select forms"
                      options={forms ?? []}
                      selected={
                        forms?.filter((f) => data.formIds?.includes(f.id)) ?? []
                      }
                      onChange={(values) =>
                        setData({
                          ...data,
                          formIds: values.map((v) => v.id),
                        })
                      }
                      getValue={(f) => f.data()?.name || f.id}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Private App Connection Modal */}
      <ConnectHubSpotPrivateAppModal
        open={showPrivateAppModal}
        onClose={() => setShowPrivateAppModal(false)}
        onSuccess={onPrivateAppConnected}
      />

      {/* Update Token Modal */}
      {updateTokenAccount && (
        <UpdateHubSpotPrivateAppTokenModal
          open={showUpdateTokenModal}
          onClose={() => {
            setShowUpdateTokenModal(false);
            setUpdateTokenAccount(null);
          }}
          connectedAccountId={updateTokenAccount.id}
          accountName={updateTokenAccount.name}
          onSuccess={onTokenUpdated}
        />
      )}
    </Modal>
  );
};
